/**
 * DesktopNavRail — Collapsible integrated navigation sidebar.
 *
 * Collapsed (60px): Icon-only rail.
 * Expanded (220px): Full sidebar with labels, user info, logo.
 *
 * Full-height, border-right design (Facebook/LinkedIn style):
 * — Flush top-to-bottom, no rounded corners, no float gap
 * — White bg, subtle right border
 * — Light blue (#EBF5FF) active pill for nav items
 */

import { useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  House,
  FileText,
  Megaphone,
  ChatCircle,
  Bell,
} from '@phosphor-icons/react';
import type { Icon as PhosphorIcon } from '@phosphor-icons/react';
import { PanelLeft, ChevronsUpDown } from 'lucide-react';
import { useLayout } from './LayoutContext';
import { useAuthStore, useEmploymentRole } from '@/features/auth';
import { usePermissions } from '@/features/auth/hooks/usePermissions';
import { useAppBranding } from '@/hooks/useAppBranding';
import { Avatar } from '@brainsway/ui';
import { useTotalUnreadCount } from '@/features/messaging/hooks';
import { NotificationBell } from '@/features/notifications';
import { UserMenu } from './UserMenu';

/** Exported so AppLayout / DesktopMessagingLayout can read the widths */
export const NAV_RAIL_COLLAPSED = '60px';
export const NAV_RAIL_EXPANDED = '220px';

/** Floating gap (px) — kept for mobile bottom nav positioning */
export const NAV_FLOAT_GAP = 12;

interface NavItem {
  path: string;
  labelKey: string;
  icon: PhosphorIcon;
  adminOnly?: boolean;
}

const navItems: NavItem[] = [
  { path: '/home', labelKey: 'nav.home', icon: House },
  { path: '/reports', labelKey: 'nav.reports', icon: FileText },
  { path: '/feed', labelKey: 'nav.feed', icon: Megaphone },
  { path: '/messages', labelKey: 'nav.chat', icon: ChatCircle },
];

/** CSS transition duration on the nav rail (ms) */
const COLLAPSE_DURATION = 300;

export function DesktopNavRail() {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { sidebarCollapsed, setSidebarCollapsed, toggleSidebar, sidebarSide } = useLayout();
  const user = useAuthStore((s) => s.user);
  const unreadCount = useTotalUnreadCount();
  const { canCreateReports } = useEmploymentRole();

  const collapsed = sidebarCollapsed;
  const isOnMessages = location.pathname.startsWith('/messages');

  const { canAccessAdmin } = usePermissions();
  const { slogoLine1, slogoLine2 } = useAppBranding();
  const isDev = import.meta.env.VITE_APP_ENV === 'development';

  const filteredItems = navItems.filter((item) => {
    if (item.path === '/reports' && !canCreateReports) return false;
    if (item.adminOnly && !canAccessAdmin()) return false;
    return true;
  });

  const handleNavClick = useCallback(
    (e: React.MouseEvent, path: string) => {
      if (path === '/messages' && !collapsed && !isOnMessages) {
        e.preventDefault();
        setSidebarCollapsed(true);
        setTimeout(() => navigate('/messages'), COLLAPSE_DURATION);
      }
    },
    [collapsed, isOnMessages, setSidebarCollapsed, navigate],
  );

  return (
    <nav
      dir="ltr"
      className={`fixed bottom-0 z-40 hidden flex-col transition-all duration-300 ease-in-out md:flex ${sidebarSide === 'right' ? 'right-0 border-l' : 'left-0 border-r'}`}
      style={{
        top: 'var(--view-as-offset, 0px)',
        width: collapsed ? NAV_RAIL_COLLAPSED : NAV_RAIL_EXPANDED,
        backgroundColor: 'var(--user-nav-bg)',
        borderColor: 'var(--user-nav-border)',
      }}
    >
      {/* Header area */}
      <div className={`relative flex h-14 flex-shrink-0 items-center ${collapsed ? 'justify-center' : 'justify-between px-4'}`}>
        <div className="absolute bottom-0 left-3 right-3 h-px bg-gray-300" />
        <Link to="/home" className={`flex items-center gap-1.5 ${collapsed ? 'cursor-pointer' : 'cursor-pointer'}`} onClick={collapsed ? (e) => { e.preventDefault(); toggleSidebar(); } : undefined}>
          <img src="/images/BrainsWayLogo.png" alt="" className="h-8 flex-shrink-0" />
          {!collapsed && (
            <span className="text-[10px] font-bold tracking-wider uppercase leading-tight">
              <span className="text-[#1a3a6b]">{slogoLine1}</span><br/>
              <span className="text-[#5ba3d9]">{slogoLine2}</span>
            </span>
          )}
        </Link>
        {!collapsed && (
          <button
            onClick={toggleSidebar}
            className="flex h-7 w-7 items-center justify-center rounded-lg text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
            aria-label={t('common.collapseSidebar')}
          >
            <PanelLeft size={16} strokeWidth={2} />
          </button>
        )}
      </div>

      {/* Nav items */}
      <div className={`flex flex-1 flex-col gap-1 overflow-y-auto overflow-x-hidden ${collapsed ? 'items-center px-2 pt-4' : 'px-3 pt-4'}`}>
        {filteredItems.map((item) => {
          const isActive = location.pathname.startsWith(item.path);
          const IconComp = item.icon;

          return (
            <Link
              key={item.path}
              to={item.path}
              onClick={(e) => handleNavClick(e, item.path)}
              title={collapsed ? t(item.labelKey) : undefined}
              className={`relative flex items-center gap-3 rounded-lg transition-all duration-200 ${
                collapsed
                  ? 'h-10 w-10 justify-center'
                  : 'px-3 py-2'
              } ${
                isActive
                  ? ''
                  : 'nav-hover-user'
              }`}
              style={{
                color: isActive ? 'var(--user-nav-active-text)' : 'var(--user-nav-text)',
                backgroundColor: isActive ? 'var(--user-nav-active-bg)' : undefined,
              }}
            >
              <span className={`relative flex-shrink-0 transition-all duration-200 ${isActive ? 'scale-105' : ''}`}>
                <IconComp size={22} weight={isActive ? 'fill' : 'regular'} />
                {item.path === '/messages' && unreadCount > 0 && (
                  <span
                    className="absolute flex items-center justify-center rounded-full bg-red-500 text-[7px] font-bold text-white ring-2 ring-white"
                    style={{
                      top: '-4px',
                      insetInlineEnd: '-6px',
                      width: '18px',
                      height: '18px',
                    }}
                  >
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
              </span>
              {!collapsed && (
                <span className={`truncate text-sm tracking-tight ${isActive ? 'font-semibold' : 'font-medium'}`}>
                  {t(item.labelKey)}
                </span>
              )}
            </Link>
          );
        })}

        {/* Notification bell — nav rail style */}
        <NotificationBell
          renderTrigger={({ onClick, unreadCount: bellUnread }) => (
            <button
              type="button"
              onClick={onClick}
              title={collapsed ? t('nav.notifications') : undefined}
              className={`relative flex w-full items-center gap-3 rounded-lg transition-all duration-200 nav-hover-user ${
                collapsed
                  ? 'h-10 w-10 justify-center'
                  : 'px-3 py-2'
              }`}
              style={{ color: 'var(--color-label-primary)' }}
            >
              <span className="relative flex-shrink-0 transition-all duration-200">
                <Bell size={22} weight="regular" />
                {bellUnread > 0 && (
                  <span
                    className="absolute flex items-center justify-center rounded-full bg-red-500 text-[7px] font-bold text-white ring-2 ring-white"
                    style={{
                      top: '-4px',
                      insetInlineEnd: '-6px',
                      width: '18px',
                      height: '18px',
                    }}
                  >
                    {bellUnread > 9 ? '9+' : bellUnread}
                  </span>
                )}
              </span>
              {!collapsed && (
                <span className="truncate text-sm font-medium tracking-tight">{t('nav.notifications')}</span>
              )}
            </button>
          )}
        />
      </div>

      {/* Dev environment ribbon */}
      {isDev && !collapsed && (
        <div className="flex-shrink-0 px-3 pb-2">
          <div
            className="rounded-xl px-3 py-2 text-center"
            style={{
              background: 'linear-gradient(135deg, rgba(255,180,50,0.25) 0%, rgba(255,120,60,0.2) 50%, rgba(255,180,50,0.25) 100%)',
              backdropFilter: 'blur(12px) saturate(180%)',
              border: '1px solid rgba(255,160,60,0.35)',
              boxShadow: '0 2px 8px rgba(255,140,50,0.15), inset 0 1px 0 rgba(255,255,255,0.4)',
            }}
          >
            <p
              className="text-[10px] font-black uppercase tracking-[0.2em]"
              style={{
                background: 'linear-gradient(135deg, #c45e00 0%, #e08a00 50%, #c45e00 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              Development Env
            </p>
          </div>
        </div>
      )}
      {isDev && collapsed && (
        <div className="flex-shrink-0 flex justify-center pb-2">
          <div
            className="flex h-8 w-8 items-center justify-center rounded-lg"
            title="Development Environment"
            style={{
              background: 'linear-gradient(135deg, rgba(255,180,50,0.25) 0%, rgba(255,120,60,0.2) 100%)',
              backdropFilter: 'blur(12px)',
              border: '1px solid rgba(255,160,60,0.35)',
            }}
          >
            <span className="text-[9px] font-black" style={{ color: '#c45e00' }}>D</span>
          </div>
        </div>
      )}

      {/* User section at bottom */}
      {user && (
        <div dir="ltr" className={`flex-shrink-0 border-t border-gray-100 ${collapsed ? 'flex justify-center py-3' : 'p-3'}`}>
          <UserMenu
            renderTrigger={({ onClick }) => (
              <button
                type="button"
                onClick={onClick}
                title={collapsed ? `${user.firstName} ${user.lastName}` : undefined}
                className={`flex items-center gap-2.5 rounded-lg transition-colors nav-hover-user ${collapsed ? 'justify-center' : 'p-2 w-full'}`}
              >
                <Avatar
                  src={user.avatarUrl}
                  name={`${user.firstName} ${user.lastName}`}
                  size="sm"
                  className="flex-shrink-0"
                />
                {!collapsed && (
                  <>
                    <div className="min-w-0 flex-1 text-start">
                      <p className="truncate text-[13px] font-bold text-gray-900">
                        {user.firstName} {user.lastName}
                      </p>
                      <p className="truncate text-[11px] text-gray-500">
                        {user.email}
                      </p>
                    </div>
                    <ChevronsUpDown size={14} className="flex-shrink-0 text-gray-400" />
                  </>
                )}
              </button>
            )}
          />
        </div>
      )}
    </nav>
  );
}
